import { useEffect, useRef, useState, useMemo } from "react";

// formik & validate
import { ErrorMessage, Form, Formik } from "formik";
import { serviceItemValidationSchema } from "../../../config/Validate";

// prime react component
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import { Toolbar } from "primereact/toolbar";

// component data import
import { serviceItemListColumn } from "../../../appconfig/DataTableColumns";
import {
  defaultPageRowOptions,
  defaultRowOptions,
  itemDropdownItems,
  itemTypeOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../../appconfig/Settings";
import {
  getAllDetails,
  postAllData,
  updateData,
} from "../../../service/ProductService";
import { MasterTabs } from "../MasterTabs";

// api constant
import {
  ADD_SERVICEITEM,
  EDIT_SERVICEITEM,
  GET_SERVICEITEM_LIST,
} from "../../../config/ApiConstant";
import { globalNameFilterTemplate } from "../../../appconfig/AppHelper";

// interface
import { ServiceItemType } from "../../../appconfig/interfaces";
import { amountTemplate, globalFieldTemplate } from "../../../appconfig/TemplateSetting";

const ServiceItem = () => {
  document.title = "Service Item | Agriown";

  const toast = useRef<any>(null);
  const formikRef = useRef<any>(null);

  // state
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [itemFilterValue, setItemFilterValue] = useState<any>(null);
  const [serviceItemList, setServiceItemList] = useState<any>([]);
  const [addServiceItemDialog, setServiceItemDialog] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedServiceItem, setSelectedServiceItem] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoad, setPageLoad] = useState<boolean>(false);

  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    item_type: { value: null, matchMode: FilterMatchMode.EQUALS },
    item_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    item_price: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // initial value for the form
  const formInitialValues: ServiceItemType = useMemo(
    () => ({
      item_type: selectedServiceItem?.item_type ?? "",
      item_name: selectedServiceItem?.item_name ?? "",
      item_name_hi: selectedServiceItem?.item_name_hi ?? "",
      item_name_gu: selectedServiceItem?.item_name_gu ?? "",
      item_price: selectedServiceItem?.item_price ?? "",
    }),
    [selectedServiceItem]
  );

  // load list on mount
  useEffect(() => {
    getServiceItemList();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // function to get service list item
  const getServiceItemList = async () => {
    try {
      const response = await getAllDetails(GET_SERVICEITEM_LIST);
      if (response) {
        setServiceItemList(response);
        setPageLoad(true);
      } else {
        setServiceItemList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // function to handle form submission
  const onSubmit = async (values: any, { resetForm }: any) => {
    try {
      setLoading(true);

      const payload: any = {
        item_name: values.item_name,
        item_type: values.item_type,
        item_price: 0,
      };

      if (values.item_name_hi?.trim()) {
        payload.item_name_hi = values.item_name_hi;
      }
      if (values.item_name_gu?.trim()) {
        payload.item_name_gu = values.item_name_gu;
      }

      const response = isEditMode
        ? await updateData(EDIT_SERVICEITEM, selectedServiceItem.id, payload)
        : await postAllData(ADD_SERVICEITEM, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        setServiceItemDialog(false);
        getServiceItemList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // function to hide the dialog
  const hideAddServiceItemDialog = () => {
    setServiceItemDialog(false);
    setIsEditMode(false);
    setSelectedServiceItem(null);
  };

  // action body template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        className="mr-2 p-button-secondary"
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
        onClick={() => {
          setSelectedServiceItem(rowData);
          setIsEditMode(true);
          setServiceItemDialog(true);
        }}
      />
    </div>
  );

  // footer content for the dialog
  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={hideAddServiceItemDialog}
        className="p-button-secondary"
      />
      <Button
        label={isEditMode ? "Update" : "Save"}
        icon="pi pi-check"
        type="button"
        loading={loading}
        disabled={loading}
        onClick={() => formikRef.current?.handleSubmit()}
        autoFocus
      />
    </div>
  );

  // function to filter by type
  const filterByType = (options: any) => (
    <Dropdown
      filter
      value={itemFilterValue}
      options={itemDropdownItems}
      onChange={(e) => {
        setItemFilterValue(e.value);
        options.filterCallback(e.value ? e.value.code : null, options.index);
      }}
      optionLabel="name"
      placeholder="Select Type"
      className="order-drp"
      showClear
    />
  );

  return (
    <>
      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <MasterTabs index={2} />
            <Toast ref={toast} />

            <Toolbar
              left={() => (
                <>
                  <span className="mr-0 md:mr-3 search-bar">
                    <i className="pi pi-search"></i>
                    <InputText
                      type="search"
                      className="search-input"
                      value={globalFilter}
                      onInput={(e) => setGlobalFilter(e.currentTarget.value)}
                      placeholder="Search..."
                    />
                  </span>
                </>
              )}
              right={() => (
                <>
                  <Button
                    icon="pi pi-filter-slash"
                    label="Clear"
                    onClick={() => {
                      setFilters({ ...emptyFilters });
                      setItemFilterValue(null);
                    }}
                    className="md:mr-2 p-button-outlined p-button-primary"
                  />
                  <Button
                    label="Add Service Item"
                    icon="pi pi-plus"
                    className="ml-2 p-button toolbar-button"
                    onClick={() => (
                      setServiceItemDialog(true),
                      setIsEditMode(false),
                      setSelectedServiceItem(null)
                    )}
                  />
                </>
              )}
            />

            {pageLoad ? (
              <DataTable
                value={serviceItemList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                globalFilterFields={['item_type', 'item_name', 'item_price']}
                rows={defaultRowOptions}
                paginator={serviceItemList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatble-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                emptyMessage="No Data Found"
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                removableSort
              >
                {serviceItemListColumn.map((col) =>
                  col.field === "action" ? (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={actionBodyTemplate}
                    />
                  ) : col.field === "item_type" ? (
                    <Column
                      key={col.field}
                      field={col.field}
                      body={globalFieldTemplate}
                      sortable
                      filter
                      filterElement={filterByType}
                      header={col.header}
                    />
                  ) : col.field === "item_name" ? (
                    <Column
                      key={col.field}
                      field={col.field}
                      sortable
                      body={globalFieldTemplate}
                      filter
                      filterElement={globalNameFilterTemplate}
                      header={col.header}
                    />
                  ) : col.field === "item_price" ? (
                    <Column
                      key={col.field}
                      field={col.field}
                      sortable
                      body={amountTemplate}
                      header={col.header}
                    />
                  ) : (
                    <Column
                      key={col.field}
                      sortable
                      field={col.field}
                      header={col.header}
                    />
                  )
                )}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {serviceItemListColumn.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>

      <Dialog
        visible={addServiceItemDialog}
        style={{ width: "700px" }}
        className="mx-3 lg:mx-0"
        header={isEditMode ? "Edit Service Item" : "Add Service Item"}
        draggable={false}
        modal
        onHide={hideAddServiceItemDialog}
        footer={footerContent}
      >
        <Formik
          enableReinitialize
          innerRef={formikRef}
          initialValues={formInitialValues}
          validationSchema={serviceItemValidationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form className="p-fluid">
              <div className="grid p-fluid formgrid">
                {/* Type */}
                <div className="field md:col-6 col-12">
                  <label htmlFor="type">Select Type *</label>
                  <Dropdown
                    id="type"
                    name="item_type"
                    value={values.item_type}
                    options={itemTypeOptions}
                    onChange={(e) => setFieldValue("item_type", e.value || "")}
                    onBlur={() => setFieldTouched("item_type", true)}
                    placeholder="Select Type..."
                    className={
                      touched.item_type && errors.item_type ? "p-invalid" : ""
                    }
                  />
                  <small className="p-error">
                    <ErrorMessage name="item_type" />
                  </small>
                </div>

                {/* Name */}
                <div className="field md:col-6 col-12">
                  <label htmlFor="service_name">Name (English) *</label>
                  <InputText
                    id="service_name"
                    name="item_name"
                    value={values.item_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    className={
                      touched.item_name && errors.item_name ? "p-invalid" : ""
                    }
                  />
                  <small className="p-error">
                    <ErrorMessage name="item_name" />
                  </small>
                </div>

                {/* Hindi Name (Optional) */}
                <div className="field md:col-6 col-12">
                  <label htmlFor="item_name_hi">Name (Hindi)</label>
                  <InputText
                    id="item_name_hi"
                    name="item_name_hi"
                    value={values.item_name_hi}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                  />
                </div>

                {/* Gujarati Name (Optional) */}
                <div className="field md:col-6 col-12">
                  <label htmlFor="item_name_gu">Name (Gujarati)</label>
                  <InputText
                    id="item_name_gu"
                    name="item_name_gu"
                    value={values.item_name_gu}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                  />
                </div>

                {/* Price */}
                {/* <div className="field md:col-6 col-12">
                  <label htmlFor="price">Price *</label>
                  <div className="flex-1 input-effect-div p-inputgroup">
                    <span className="font-bold p-inputgroup-addon">₹</span>
                    <InputText
                      keyfilter="int"
                      id="price"
                      name="item_price"
                      onBlur={handleBlur}
                      value={values.item_price}
                      onChange={handleChange}
                      autoComplete="off"
                      className={
                        touched.item_price && errors.item_price
                          ? "p-invalid"
                          : ""
                      }
                    />
                  </div>
                  <small className="p-error">
                    <ErrorMessage name="item_price" />
                  </small>
                </div> */}
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default ServiceItem;
